import dayjs from 'dayjs'
import numeral from 'numeral'
import { areEquals, combineFilters, packFilter } from './Filter'
import { DEFAULT_CHART_PREFERENCES } from '../screens/Explorer/DrawerTabs/StyleTab/styleConsts'
import { SORTING } from './consts'
import _ from 'lodash'
import { getFormatter } from './KPI/utils'
import { getWorkspaceTheme } from './utils'

export const backwardsFlag = '2.1.8'

/**
 * Class contains the following:
 *
 * config:                      current chart configuration settings
 * result:                      last server result
 * series:                      current data series
 * drillAttributes:             possible drill attributes
 * valueAxis:                   the value axis
 * categories:                  current categories
 * allowedKPIs:                 KPIs that can be selected and work with existing drill attributes and categories
 * allowedReportSeriesTables    reportSeriesTables that have drill attribute and category
 */
const calculatePadding = (inputString) => {
    const textSize = 4
    const minLength = 20
    if (!inputString || inputString.length < minLength) return 0
    return (inputString.length - minLength) * textSize
}

export default class ChartState {
    static getDefaultConfig() {
        return {
            chartTitle: '',
            selectedItems: [],
            selectedDates: new Map(),
            seriesType: 'column',
            stacked: true,
            seriesTypeMap: new Map(),
            categoryAxis: 'Time',
            sortColumns: [
                {
                    column: '',
                    order: SORTING.NAT,
                },
            ],
            period: 'Monthly',
            timeRange: 'All',
            calendarType: 'Rolling',
            drillAttribute: undefined,
            worksheetColumns: [],
            selectedTableColumns: [],
            nonPivotColumnsOrder: [],
            pivotColumnsOrder: [],
            selectedTableKpis: [],
            usedDrillAttributes: [],
            from: null,
            to: null,
            filter: null,
            savedFilter: null,
            view: 'chart',
            loading: false,
            worksheetID: undefined,
            rangeName: undefined,
            sheetName: undefined,
            selectedWorksheetName: undefined,
            activePrompts: [],
            tableState: {
                nonPivotState: { rowGroupCols: [] },
                pivotState: { rowGroupCols: [], pivotCols: [] },
                pinnedCols: [],
            },
            selectedTables: [],
            displayRows: 'All',
            tableTimeSeries: true,
            disableTimeSeries: false,
            allowedKPIs: undefined,
            allowedReportSeriesTables: undefined,
            styleOverrides: _.cloneDeep(DEFAULT_CHART_PREFERENCES),
            themeID: undefined,
            kpiCompareType: 'percentage',
            kpiCompareTarget: undefined,
            metricsOrder: [],
            backwardsFlag: backwardsFlag,
        }
    }

    constructor(server, config, setConfig) {
        this.server = server
        this.categories = undefined
        this.config = config
        this.setConfig = setConfig
    }

    hasData() {
        return this.series?.length > 0
    }

    clear() {
        this.series = null
        this.result = null
        const newConfig = ChartState.getDefaultConfig()
        this.config = { ...newConfig }
        this.setConfig({ ...newConfig })
    }

    processData(sType, result) {
        let newSeries
        if (sType === 'scatter') {
            newSeries = this.processScatter(sType, result)
        } else {
            newSeries = []
            for (let i = 0; i < result.series?.length; i++) {
                let metric
                for (let j = 0; j < result.metrics?.length; j++) {
                    if (result.metrics[j].name === result.series[i].metric) {
                        metric = result.metrics[j]
                        break
                    }
                }
                let s = {
                    type: sType,
                    metric: result.series[i].metric,
                    categoryIndex:
                        result.categories?.length === 1 ? i : result.series[i].categoryIndex,
                    category:
                        result.categories?.length === 1 && result.categories[0] === 'All'
                            ? null
                            : result.categories[result.series[i].categoryIndex],
                    name: result.series[i].name,
                    format: metric ? metric.format : null,
                    data: [],
                }
                if (sType !== 'bar') {
                    s.yAxisIndex = result.series[i].axis
                } else {
                    s.xAxisIndex = result.series[i].axis
                }
                if (result.categoryAxisValues) {
                    this.categoryAxisValues = result.categoryAxisValues
                    for (let j = 0; j < result.series[0].data?.length; j++) {
                        s.data.push(result.series[i].data[j])
                    }
                } else {
                    for (let j = 0; j < result.dates?.length; j++) {
                        s.data.push([result.dates[j], result.series[i].data[j]])
                    }
                }
                newSeries.push(s)
            }
        }
        var newAxes = []
        for (var i = 0; i < result.axes?.length; i++) {
            const axis = result.axes[i]
            var chartAxis = {
                name: axis.name ? this.axisNameFormatter(axis.name) : undefined,
                type: 'value',
                alignTicks: true,
                axisLabel: {
                    formatter: (val) => {
                        if (Math.abs(val) > 999999)
                            return getFormatter(axis.format, true).format(val)
                        return getFormatter(axis.format).format(val)
                    },
                },
            }
            if (sType !== 'scatter') {
                if (this.config.metricsOrder.length > 0) {
                    let pos = this.config.metricsOrder.find((m) => {
                        if (axis.name.includes('/')) {
                            return (
                                m.name.includes(axis.name.split('/')[0].trim()) &&
                                m.name.includes(axis.name.split('/')[1].trim())
                            )
                        } else {
                            return m.name === axis.name
                        }
                    })?.position
                    if (!pos) {
                        if (i > 0) {
                            pos = 'right'
                        } else {
                            pos = 'left'
                        }
                    }
                    if (sType === 'bar') {
                        if (pos === 'left') pos = 'top'
                        if (pos === 'right') pos = 'bottom'
                        chartAxis.position = pos
                        chartAxis.axisLabel.align = pos === 'bottom' ? 'top' : 'bottom'
                    } else {
                        chartAxis.position = pos
                        chartAxis.nameTextStyle = {
                            //padding: [0, 0, 0, calculatePadding(axis.name)],
                        }
                        chartAxis.axisLabel.align = pos === 'right' ? 'left' : 'right'
                    }
                } else {
                    if (sType === 'bar') {
                        chartAxis.position = 'top'
                        chartAxis.axisLabel.align = 'bottom'
                    } else {
                        chartAxis.position = 'left'
                        chartAxis.nameTextStyle = {
                            //padding: [0, 0, 0, calculatePadding(axis.name)],
                        }
                        chartAxis.axisLabel.align = 'right'
                    }
                    if (i > 0) {
                        if (sType === 'bar') {
                            chartAxis.position = 'bottom'
                            chartAxis.axisLabel.align = 'top'
                        } else {
                            chartAxis.position = 'right'
                            chartAxis.nameTextStyle = {
                                //padding: [0, calculatePadding(axis.name), 0, 0],
                            }
                            chartAxis.axisLabel.align = 'left'
                        }
                        if (i > 1) {
                            chartAxis.offset = 80 * (i - 1)
                        }
                    }
                }
            }
            newAxes.push(chartAxis)
        }
        this.valueAxis = newAxes
        if (result.drillAttributes) this.drillAttributes = result.drillAttributes
        this.changeDrillAttributes = result.changeDrillAttributes
        this.categories = result.categories
        this.series = newSeries
        if (result.categoryAxis) this.config.categoryAxis = result.categoryAxis
        if (sType === this.config.seriesType && newSeries && newSeries?.length > 0)
            this.configureSeries(sType, this.config)
        this.config.allowedKPIs = result.allowedKPIs
        this.config.allowedReportSeriesTables = result.allowedReportSeriesTables
        this.setConfig({ ...this.config })
        return newSeries
    }

    processScatter(sType, result) {
        // For scatter plots, we need at least axes 0 and 1 (X and Y)
        const hasRequiredAxes =
            result.series.some((s) => s.axis === 0) && result.series.some((s) => s.axis === 1)
        if (!hasRequiredAxes) return []

        // Check if we have a 3rd axis for bubble chart
        const isBubbleChart = result.series.some((s) => s.axis === 2)
        const metricsToUse = isBubbleChart ? 3 : 2

        // Get unique categories
        const uniqueCategories = this.getUniqueCategories(result)
        // Create one series per category
        return uniqueCategories.map(({ categoryIndex, categoryName }) => {
            // Find data series for each metric in this category
            const metricSeries = this.findMetricSeriesForCategory(
                result,
                categoryIndex,
                metricsToUse
            )
            // Create the series object
            const series = {
                type: sType,
                categoryIndex: categoryIndex,
                name: categoryName,
                data: [],
                symbolSize: isBubbleChart
                    ? (val) => {
                          const size = val[2] || 0
                          const minSize = 5
                          const maxSize = 50

                          // Check if this is a percentage value (0-1 range)
                          // Find the Z-axis format to determine if it's a percentage
                          const zAxis = result.axes?.find((a) => a.index === 2)
                          const isPercentage = zAxis?.format?.includes('%')

                          let scaledSize
                          if (isPercentage) {
                              // For percentages (0-1), scale directly
                              // 0% = minSize, 100% = maxSize
                              scaledSize = minSize + size * (maxSize - minSize)
                          } else {
                              // For other values, use logarithmic scaling
                              scaledSize = Math.log(size + 1) * 3 + minSize
                          }

                          return Math.max(minSize, Math.min(scaledSize, maxSize))
                      }
                    : 10, // Fixed size for regular scatter
            }
            // Add format information for tooltips based on axis order
            series.formats = metricSeries.map((s) => {
                if (!s) return ''
                // Find the corresponding axis to get the format
                const axis = result.axes?.find((a) => a.index === s.axis)
                return axis?.format || ''
            })
            // Build data points
            series.data = this.buildScatterDataPoints(metricSeries, metricsToUse)

            return series
        })
    }

    getUniqueCategories(result) {
        // Handle single category case
        if (result.categories?.length === 1 && result.categories[0] === 'All') {
            return [{ categoryIndex: 0, categoryName: null }]
        }
        // Get unique category indices from all series
        const uniqueCategoryIndices = [
            ...new Set(result.series.map((s) => s.categoryIndex)),
        ].filter((index) => index != null)
        return uniqueCategoryIndices.map((index) => ({
            categoryIndex: index,
            categoryName: result.categories?.[index] || null,
        }))
    }

    findMetricSeriesForCategory(result, categoryIndex, metricsToUse) {
        // Create an array to hold series in their axis order (0=X, 1=Y, 2=Size)
        const metricSeries = [null, null, null]

        // Find series for this category and place them according to their axis property
        result.series.forEach((series) => {
            if (series.categoryIndex === categoryIndex && series.axis < 3) {
                metricSeries[series.axis] = series
            }
        })

        // Return only the number of series we need
        return metricSeries.slice(0, metricsToUse)
    }

    buildScatterDataPoints(metricSeries, metricsToUse) {
        // Find the maximum data length across all metrics
        const maxDataLength = Math.max(
            ...metricSeries.filter((s) => s?.data).map((s) => s.data.length),
            0
        )
        const dataPoints = []
        // Build data points for each row
        for (let row = 0; row < maxDataLength; row++) {
            const point = []
            // Add value for each metric (x, y, and optionally size)
            for (let metricIndex = 0; metricIndex < metricsToUse; metricIndex++) {
                const series = metricSeries[metricIndex]
                const value = series?.data?.[row] ?? 0 // Default to 0 if missing
                point.push(value)
            }
            dataPoints.push(point)
        }
        return dataPoints
    }

    processServerResult(serverResult, object, loadingCallback) {
        if (serverResult === 'No time series found') {
            object.config.loading = false
            this.setConfig({ ...object.config })
            return
        }
        object.result = serverResult
        object.processData(object.config.seriesType, serverResult)
        object.config.loading = false
        if (serverResult.timeSeriesNotFound) {
            object.config.tableTimeSeries = false
            object.config.disableTimeSeries = true
        }
        this.setConfig({ ...object.config })
        if (loadingCallback) loadingCallback()
    }

    handleServerCall(result, object, config, loadingCallback) {
        if (result.dataset) {
            this.processServerResult(result.dataset, object, loadingCallback)
        } else {
            this.processServerResult(result, object, loadingCallback)
        }
    }

    getResults(config, loadingCallback, prompts) {
        if (!this.server) return
        this.config = config
        this.config.loading = true
        let {
            calendarType,
            selectedItems,
            period,
            drillAttribute,
            filter,
            savedFilter,
            selectedDates,
            categoryAxis,
            timeRange,
            view,
            sortColumns,
            displayRows,
        } = config
        let metrics = []
        for (let i = 0; i < selectedItems?.length; i++) {
            let metric
            if (selectedItems[i].worksheetID) {
                metric = selectedItems[i]
            } else if (selectedItems[i].kpi) {
                metric = {
                    metricName: selectedItems[i].kpi,
                    reportSeriesTableID: selectedItems[i].reportSeriesTableID,
                }
            } else {
                metric = {
                    measureName: selectedItems[i].columnName,
                    reportSeriesTableID: selectedItems[i].reportSeriesTableID,
                }
            }
            if (selectedDates) {
                if (selectedItems[i].worksheetID) {
                    let byDate = selectedDates.get(
                        selectedItems[i].worksheetID + ':' + selectedItems[i].measureName
                    )
                    if (byDate) metric.byDate = byDate
                } else {
                    let byDate = selectedDates.get(
                        selectedItems[i].reportSeriesTableID + ':' + selectedItems[i].columnName
                    )
                    if (byDate) metric.byDate = byDate
                }
            }
            metrics.push(metric)
        }
        let action
        if (categoryAxis === 'Time' && !config.disableTimeSeries) {
            action = {
                action: 'getTimeSeries',
                metrics: metrics,
                calendarType: calendarType,
                period: period,
                split: view === 'chart',
                process: false,
            }
        } else {
            action = {
                action: 'getDataset',
                metrics: metrics,
                period: period,
                categoryAxis: categoryAxis,
            }
            if (sortColumns.some((col) => col.column)) {
                action.sortColumns = sortColumns
            }
        }
        if (timeRange) {
            action.timeRange = timeRange
            if (action.timeRange === 'Custom') {
                if (this.config.fromPeriod) action.fromPeriod = this.config.fromPeriod
                if (this.config.toPeriod) action.toPeriod = this.config.toPeriod
                if (!this.config.fromPeriod && !this.config.toPeriod) {
                    if (this.config.from) {
                        action.start = dayjs(this.config.from).format('MM/DD/YYYY')
                    }
                    if (this.config.to) {
                        action.end = dayjs(this.config.to).format('MM/DD/YYYY')
                    }
                }
            }
        }
        if (prompts?.length > 0) {
            const validPrompts = []
            prompts.forEach((prompt) => {
                if (Array.isArray(prompt)) {
                    prompt.forEach((p) => {
                        if (p.filterValue.values.length > 0 && p.filterValue.values[0] !== 'All')
                            validPrompts.push(p)
                    })
                } else {
                    if (
                        prompt.filterValue.values.length > 0 &&
                        prompt.filterValue.values[0] !== 'All'
                    )
                        validPrompts.push(prompt)
                }
            })
            action.prompts = packFilter(validPrompts)
        }
        if (drillAttribute) action.drillAttribute = drillAttribute
        if (displayRows) action.limit = displayRows
        if (filter || savedFilter) {
            if (savedFilter) {
                action.filter = combineFilters(filter, savedFilter.filter)
            } else {
                action.filter = filter
            }
        } else {
            action.filter = undefined
        }
        if (action.metrics.some((m) => m.worksheetID)) {
            this.server.sheetPostData(
                action,
                (result, object) => this.handleServerCall(result, object, config, loadingCallback),
                this
            )
        } else {
            this.server.postData(
                action,
                (result, object) => this.handleServerCall(result, object, config, loadingCallback),
                this
            )
        }
    }

    configureSeries(newSeriesType, config) {
        if (!newSeriesType) newSeriesType = 'column'
        this.config = config
        if (!this.series) return
        let numDataPoints = this.series[0].data?.length
        if (
            (this.config.seriesType === 'scatter' && newSeriesType !== 'scatter') ||
            (this.config.seriesType !== 'scatter' && newSeriesType === 'scatter') ||
            (this.config.seriesType === 'bar' && newSeriesType !== 'bar') ||
            (this.config.seriesType === 'heatmap' && newSeriesType !== 'heatmap')
        ) {
            this.series = this.processData(newSeriesType, this.result)
        }
        this.config.seriesType = newSeriesType
        let numSeries = this.series?.length
        let newSeries = []
        for (let i = 0; i < numSeries; i++) {
            if (this.series[i].waterfallPlaceholder) continue
            newSeries.push(this.series[i])
        }
        this.series = newSeries
        numSeries = this.series?.length
        for (let i = 0; i < numSeries; i++) {
            var t = this.config.seriesTypeMap
                ? this.config.seriesTypeMap.get(this.series[i].metric)
                : undefined
            var type = t ? t : this.config.seriesType
            switch (type) {
                case 'bar':
                case 'column':
                    this.series[i].type = 'bar'
                    if (this.config.stacked) {
                        if (this.categories && this.categories?.length > 1) {
                            this.series[i].stack = this.series[i].metric
                        } else {
                            this.series[i].stack = 'All'
                        }
                    } else {
                        this.series[i].stack = undefined
                    }
                    this.series[i].itemStyle = {
                        emphasis: {
                            barBorderRadius: [3, 3],
                        },
                        normal: {
                            barBorderRadius: [3, 3, 3, 3],
                        },
                    }
                    this.series[i].areaStyle = undefined
                    if (numDataPoints && numDataPoints > 50) {
                        this.series[i].barGap = '0%'
                        this.series[i].barCategoryGap = '40%'
                    } else if (numDataPoints && numDataPoints > 20) {
                        this.series[i].barGap = '10%'
                        this.series[i].barCategoryGap = '40%'
                    } else {
                        this.series[i].barGap = '10%'
                        this.series[i].barCategoryGap = '40%'
                    }
                    break
                case 'line':
                    this.series[i].type = 'line'
                    this.series[i].stack = undefined
                    this.series[i].areaStyle = undefined
                    this.series[i].barGap = undefined
                    this.series[i].barCategoryGap = undefined
                    break
                case 'area':
                    this.series[i].type = 'line'
                    this.series[i].stack = config.stacked ? 'all' : undefined
                    this.series[i].areaStyle = {}
                    this.series[i].barGap = undefined
                    this.series[i].barCategoryGap = undefined
                    break
                case 'pie':
                case 'donut':
                    this.series[i].type = 'pie'
                    this.series[i].stack = 'all'
                    this.series[i].areaStyle = {}
                    this.series[i].barGap = undefined
                    this.series[i].barCategoryGap = undefined
                    if (this.series[i].data.some((d) => !d?.name)) {
                        this.series[i].data = [...this.series[i].data].map((d, j) => ({
                            value: d,
                            name: this.categoryAxisValues ? this.categoryAxisValues[j] : '',
                        }))
                    }
                    break
                case 'radialBar':
                    if (this.series[i]?.data?.some((d) => !d.name)) {
                        this.series[i].data = [...this.series[i].data].map((d, j) => ({
                            value: d,
                            name: this.categoryAxisValues ? this.categoryAxisValues[j] : '',
                        }))
                    }
                    break
                case 'pictorialBar':
                    this.series[i].type = 'pictorialBar'
                    break
                case 'scatter':
                    this.series[i].type = 'scatter'
                    this.series[i].stack = undefined
                    this.series[i].areaStyle = {}
                    this.series[i].barGap = undefined
                    this.series[i].barCategoryGap = undefined
                    break
                case 'waterfall':
                    this.series[i].type = 'bar'
                    this.series[i].stack = 'all'
                    this.series[i].itemStyle = { color: 'transparent' }
                    let positive = {
                        name: 'positive',
                        type: 'bar',
                        stack: 'all',
                        data: [],
                        itemStyle: { color: '#009262' },
                        waterfallPlaceholder: true,
                    }
                    let negative = {
                        name: 'negative',
                        type: 'bar',
                        stack: 'all',
                        data: [],
                        itemStyle: { color: '#EB382A' }, //C33025
                        waterfallPlaceholder: true,
                    }
                    let lastVal = 0
                    let newData = []
                    for (let j = 0; j < this.series[i].data.length; j++) {
                        if (this.series[i].data[j] >= 0) {
                            if (j === 0 || j === this.series[i].data.length - 1) {
                                positive.data.push({
                                    value: this.series[i].data[j],
                                    itemStyle: { color: '#2B65D9' },
                                })
                            } else {
                                positive.data.push(this.series[i].data[j])
                            }
                            negative.data.push(0)
                            newData.push(lastVal)
                        } else {
                            if (j === 0 || j === this.series[i].data.length - 1) {
                                negative.data.push({
                                    value: -this.series[i].data[j],
                                    itemStyle: { color: '#2B65D9' },
                                })
                            } else {
                                negative.data.push(-this.series[i].data[j])
                            }
                            positive.data.push(0)
                            newData.push(lastVal + this.series[i].data[j])
                        }
                        lastVal += this.series[i].data[j]
                    }
                    this.series[i].data = newData
                    this.series.push(positive)
                    this.series.push(negative)
                    break
                default:
                    break
            }
        }
        if (this.categories) {
            let catLegendData = []
            let catIndices = []
            for (let i = 0; i < this.series?.length; i++) {
                // if (
                //     this.categories?.length > 1 &&
                //     (this.config.seriesType === 'bar' || this.config.seriesType === 'column') &&
                //     this.config.stacked
                // ) {
                //     if (catIndices.indexOf(this.series[i].categoryIndex) < 0) {
                //         let item = {
                //             name: this.series[i].name,
                //         }
                //         catLegendData.push(item)
                //         catIndices.push(this.series[i].categoryIndex)
                //     }
                // } else {
                let item = {
                    name: this.series[i].name,
                }
                catLegendData.push(item)
                catIndices.push(i)
                //}
            }
            this.categoryLegendData = catLegendData
        }
        this.setConfig({ ...config })
    }

    getOption(themeID, workspaceMetadata, slackTheme) {
        let categoryAxisSettings
        if (this.config.categoryAxis === 'Time') {
            categoryAxisSettings = {
                type: 'category',
                data: this.result.dates,
            }
        } else {
            categoryAxisSettings = {
                name: this.categoryAxis,
                type: 'category',
                data: this.categoryAxisValues,
            }
            if (this.categoryAxisValues) {
                if (this.categoryAxisValues.length > 4)
                    categoryAxisSettings.axisLabel = { rotate: 45 }
                if (this.categoryAxisValues.length > 10)
                    categoryAxisSettings.axisLabel = { rotate: 90 }
            }
        }
        let theme
        if (slackTheme) {
            theme = slackTheme
        } else {
            if (themeID) {
                this.config.themeID = themeID
                theme = getWorkspaceTheme(themeID, workspaceMetadata)
            } else {
                if (this.config.themeID)
                    theme = getWorkspaceTheme(this.config.themeID, workspaceMetadata)
                else theme = undefined
            }
        }
        const parsedSeries =
            this.config.categoryAxis === 'Time' &&
            (this.config.seriesType === 'bar' || this.config.seriesType === 'scatter')
                ? this.series.map((s) => {
                      return {
                          ...s,
                          data: s.data.map((d) => {
                              return Array.isArray(d) ? [...d].reverse() : d
                          }),
                          yAxisIndex: undefined,
                      }
                  })
                : this.series
        const option = {
            animation: false,
            series: parsedSeries,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'line',
                    crossStyle: {
                        color: '#999',
                        width: 1,
                        type: 'dashed',
                    },
                },
                order: 'valueDesc',
                confine: true,
            },
            legend: {
                orient: 'horizontal',
                top: 'bottom',
                data: this.categoryLegendData,
                tooltip: {
                    show: true,
                },
                itemGap: 10,
            },
            backgroundColor: '#FFF',
            title: {
                text: this.config.chartTitle,
                left: '50%',
            },
        }
        let darkTheme = false
        if (theme) {
            if (theme.colorScheme && theme.colorScheme.backgroundColor) {
                option.backgroundColor = theme.colorScheme.backgroundColor
                if (theme.colorScheme.darkTheme) {
                    darkTheme = true
                    option.textStyle = { color: '#FFFFFF' }
                    option.legend.textStyle = { color: '#FFFFFF' }
                }
            }
            option.color = theme.colorScheme.colors.map((c) => c.val)
        }
        if (this.config.seriesType === 'waterfall') {
            option.legend = undefined
        } else if (
            (this.config.seriesType !== 'bar' && this.config.seriesType !== 'column') ||
            !this.config.stacked
        ) {
            option.legend.formatter = function (name) {
                return name
            }
        } else {
            option.legend.formatter = function (name) {
                // var index = name.indexOf(' / ')
                // if (index >= 0) {
                //     return name.slice(index + 3)
                // } else {
                return name
                // }
            }
        }
        if (this.config.seriesType === 'bar') {
            option.xAxis = Array.isArray(this.valueAxis) ? this.valueAxis : [this.valueAxis]
            option.yAxis = [categoryAxisSettings]
        } else if (this.config.seriesType === 'scatter' && this.valueAxis) {
            // For scatter plots, always use the first two axes from valueAxis
            // First axis (index 0) goes to X-axis
            // Second axis (index 1) goes to Y-axis
            if (this.valueAxis.length >= 2) {
                option.xAxis = [this.valueAxis[0]]
                option.yAxis = [this.valueAxis[1]]
            } else if (this.valueAxis.length === 1) {
                // Fallback if only one axis exists
                option.xAxis = [this.valueAxis[0]]
                option.yAxis = [this.valueAxis[0]]
            } else {
                // Fallback if no axes exist
                option.xAxis = [
                    {
                        type: 'value',
                        alignTicks: true,
                    },
                ]
                option.yAxis = [
                    {
                        type: 'value',
                        alignTicks: true,
                    },
                ]
            }
            if (option.xAxis[0]) option.xAxis[0].position = 'bottom'
            if (option.yAxis[0]) option.yAxis[0].position = 'left'
        } else {
            option.xAxis = [categoryAxisSettings]
            option.yAxis = Array.isArray(this.valueAxis) ? this.valueAxis : [this.valueAxis]
        }
        option.xAxis.forEach((axis) => {
            if (axis?.splitLine) axis.splitLine = { show: false }
        })
        option.yAxis.forEach((axis) => {
            if (axis?.splitLine) axis.splitLine = { show: false }
        })

        const axisColor = darkTheme ? '#FFFFFF' : '#6E7079'

        option.xAxis.forEach((axis) => {
            if (axis) this.setAxisTheme(axis, axisColor)
        })
        option.yAxis.forEach((axis) => {
            if (axis) this.setAxisTheme(axis, axisColor)
        })

        if (this.config.seriesType === 'heatmap') {
            // Ensure we have the required data for heatmap rendering
            if (!this.categoryAxisValues || !this.categories || !this.result?.series?.[0]) {
                console.warn('Heatmap rendering skipped: missing required data', {
                    hasCategoryAxisValues: !!this.categoryAxisValues,
                    hasCategories: !!this.categories,
                    hasSeries: !!this.result?.series?.[0],
                })
                return option
            }

            option.xAxis = [
                {
                    type: 'category',
                    data: this.categoryAxisValues,
                    splitArea: { show: true },
                },
            ]
            option.yAxis = [
                {
                    type: 'category',
                    data: this.categories,
                    splitArea: { show: true },
                },
            ]
            option.series = [
                {
                    name: this.result.series[0].metric,
                    data: this.categories
                        .map((catValue, i) => {
                            return this.categoryAxisValues.map((cat, j) => {
                                return [cat, catValue, this.result.series[i]?.data[j]]
                            })
                        })
                        .flat(),
                    type: 'heatmap',
                    label: { show: true },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowColor: 'rgba(0, 0, 0, 0.5)',
                        },
                    },
                    format: this.result?.metrics[0]?.format || null,
                },
            ]
            this.series = option.series
            const values = option.series[0].data
                .map((row) => row[2])
                .filter((value) => value !== null)
                .map((value) => Number(value))
            const maxValue = Math.max(...values)
            option.visualMap = {
                min: 0,
                max: maxValue,
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: '5%',
            }
        }

        option.tooltip.formatter = (event) => this.tooltipFormatter(event)
        option.label = {
            formatter: (params) => this.labelFormatter(params),
        }
        return option
    }

    invertAxis() {
        this.config.inverted = !this.config.inverted
        this.checkAndFormatAxisPosition(this.valueAxis)
        this.setConfig({ ...this.config })
    }

    checkAndFormatAxisPosition(axis) {
        if (this.config.seriesType === 'column') {
            if (Array.isArray(axis)) {
                axis.forEach((a) => {
                    const newPos = a.position === 'left' ? 'right' : 'left'
                    a.position = newPos
                    a.axisLabel.align = newPos === 'left' ? 'right' : 'left'
                })
            } else {
                const newPos = axis.position === 'left' ? 'right' : 'left'
                axis.position = newPos
                axis.axisLabel.align = newPos === 'left' ? 'right' : 'left'
            }
        }
        if (this.config.seriesType === 'bar') {
            if (Array.isArray(axis)) {
                axis.forEach((a) => {
                    const newPos = a.position === 'bottom' ? 'top' : 'bottom'
                    a.position = newPos
                    a.axisLabel.align = newPos === 'bottom' ? 'top' : 'bottom'
                })
            } else {
                const newPos = axis.position === 'bottom' ? 'top' : 'bottom'
                axis.position = newPos
                axis.axisLabel.align = newPos === 'bottom' ? 'top' : 'bottom'
            }
        }
    }

    setAxisTheme(axis, color) {
        axis.nameTextStyle = axis.nameTextStyle
            ? { ...axis.nameTextStyle, color: color }
            : { color: color }
        axis.axisLabel = axis.axisLabel ? { ...axis.axisLabel, color: color } : { color: color }
        axis.axisLine = axis.axisLine
            ? { ...axis.axisLine, lineStyle: { color: color } }
            : { lineStyle: { color: color } }
    }

    labelFormatter(params) {
        const formatOrEmpty = (value, short) => {
            const formattedVal = getFormatter(
                this.series[params.seriesIndex]?.format,
                short
            )?.format(value)
            if (
                params?.data?.name &&
                (this.config.seriesType === 'pie' || this.config.seriesType === 'donut')
            )
                return `${params?.data?.name} - ${formattedVal}`
            if (value === 0) return null
            return formattedVal
        }
        if (Array.isArray(params.data)) {
            if (params.data.length === 2) {
                return formatOrEmpty(params.data[1])
            } else if (params.data.length === 3) {
                return formatOrEmpty(params.data[2], true)
            } else {
                return formatOrEmpty(params.data[0])
            }
        } else if (Array.isArray(params?.data?.value)) {
            return formatOrEmpty(params.data.value[1])
        } else if (params?.data?.value !== undefined) {
            return formatOrEmpty(params.data.value)
        } else {
            return formatOrEmpty(params.data) || ''
        }
    }

    tooltipFormatter(event) {
        if (!Array.isArray(event)) {
            event = [event]
        }
        if (this.config.seriesType === 'scatter') {
            const firstEvent = event[0]
            if (!firstEvent || !firstEvent.data) {
                return ''
            }

            let s =
                firstEvent.marker +
                '<strong>' +
                (this.config.categoryAxis === 'Time'
                    ? this.result.dates[firstEvent.dataIndex]
                    : this.result.categoryAxisValues[firstEvent.dataIndex]) +
                '</strong>' +
                (this.categories?.length > 1 ? '</br>' + firstEvent.seriesName : '') +
                '</br>'

            const formats = this.series[firstEvent.seriesIndex].formats || []
            const dataPoint = firstEvent.data || []

            // Find the axes to get their names
            const xAxis = this.result.axes?.find((a) => a.index === 0)
            const yAxis = this.result.axes?.find((a) => a.index === 1)
            const zAxis = this.result.axes?.find((a) => a.index === 2)

            // Format X and Y values
            if (dataPoint.length >= 2) {
                if (xAxis) {
                    s +=
                        xAxis.name +
                        ': ' +
                        getFormatter(formats[0] || xAxis.format).format(dataPoint[0] || 0) +
                        '</br>'
                }
                if (yAxis) {
                    s +=
                        yAxis.name +
                        ': ' +
                        getFormatter(formats[1] || yAxis.format).format(dataPoint[1] || 0)
                }

                // Handle bubble size (Z-axis) if present
                if (dataPoint.length >= 3 && dataPoint[2] != null && zAxis) {
                    s +=
                        '</br>' +
                        zAxis.name +
                        ': ' +
                        getFormatter(formats[2] || zAxis.format).format(dataPoint[2])
                }
            }

            return s
        } else {
            let s = ''
            if (
                (this.config.seriesType === 'pie' || this.config.seriesType === 'donut') &&
                this.config.categoryAxis === 'Time'
            ) {
                let sharedLabel = ''
                event.forEach((item, index) => {
                    if (item.data) {
                        if (index === 0) {
                            sharedLabel = '<strong>' + item.data.name + '</strong></br>'
                        }
                        s +=
                            item.marker +
                            getFormatter(this.series[item.seriesIndex].format).format(
                                item.data.value
                            ) +
                            '</br>'
                    }
                })
                s = sharedLabel + s
            } else if (this.config.seriesType === 'heatmap') {
                const item = event[0]
                s +=
                    item.marker +
                    getFormatter(this.series[item.seriesIndex].format).format(item.data[2]) +
                    '</br>' +
                    '<strong>' +
                    item.name +
                    '</strong></br>'
            } else {
                let sharedLabel = ''
                let seriesValues = ''
                event.forEach((item, index) => {
                    if (item.data) {
                        let categoryLabel = Array.isArray(item.data)
                            ? item.data[0]
                            : Array.isArray(item.data?.value)
                              ? item.data.value[0]
                              : item.name

                        let value = Array.isArray(item.data)
                            ? item.data[1]
                            : Array.isArray(item.data?.value)
                              ? item.data.value[1]
                              : typeof item.data === 'number'
                                ? item.data
                                : item.data?.value
                        if (value !== undefined && value !== null) {
                            sharedLabel = '<strong>' + categoryLabel + '</strong></br>'
                            seriesValues +=
                                item.marker +
                                item.seriesName +
                                ': ' +
                                getFormatter(this.series[item.seriesIndex].format).format(value) +
                                '</br>'
                        }
                    }
                })
                if (this.config.drillAttribute) {
                    const category = Array.isArray(event[0].data)
                        ? event[0].data[0]
                        : Array.isArray(event[0].data?.value)
                          ? event[0].data.value[0]
                          : event[0].name

                    let totalsBySeries = this.getTotalValuesBySeries(category)
                    const showSeriesNames =
                        this.config.categoryAxis === 'Time' ||
                        Object.keys(totalsBySeries).length > 1

                    Object.entries(totalsBySeries).forEach(([seriesName, total]) => {
                        seriesValues += showSeriesNames
                            ? `<strong>${seriesName}</strong> <strong>Total:</strong> ${getFormatter().format(total)}<br>`
                            : `<strong>Total:</strong> ${getFormatter().format(total)}<br>`
                    })
                }
                s = sharedLabel + seriesValues
            }
            return s
        }
    }

    getTotalValuesBySeries(categoryValue) {
        let totalsBySeries = {}
        const isTimeCategory = this.config.categoryAxis === 'Time'
        if (isTimeCategory) {
            this.series.forEach((seriesItem) => {
                if (Array.isArray(seriesItem.data)) {
                    seriesItem.data.forEach((item) => {
                        if (item[0] === categoryValue) {
                            totalsBySeries[seriesItem.metric] =
                                (totalsBySeries[seriesItem.metric] || 0) + item[1]
                        }
                    })
                }
            })
        } else {
            this.series.forEach((seriesItem) => {
                totalsBySeries[seriesItem.metric] =
                    (totalsBySeries[seriesItem.metric] || 0) +
                    seriesItem.data[this.categoryAxisValues.indexOf(categoryValue)]
            })
        }
        return totalsBySeries
    }

    axisNameFormatter(axisName) {
        return axisName.split('/').join('\n')
    }

    getMetric(metricName) {
        if (!this.result || !this.result.metrics) return null
        for (var i = 0; i < this.result.metrics?.length; i++) {
            if (this.result.metrics[i].name === metricName) return this.result.metrics[i]
        }
        return null
    }

    getSeries(seriesName) {
        if (!this.series) return null
        for (var i = 0; i < this.series?.length; i++) {
            if (this.series[i].name === seriesName) return this.series[i]
        }
        return null
    }

    addFilterItem(filterItem) {
        if (this.config.filter === null || this.config.filter === undefined) {
            this.config.filter = filterItem
            return filterItem
        } else if (this.config.filter.boperator) {
            this.config.filter.filters.push(filterItem)
            return this.config.filter
        } else {
            var newFilter = {
                boperator: 'And',
                filters: [this.config.filter, filterItem],
            }
            this.config.filter = newFilter
            return newFilter
        }
    }

    removeFilterItem(filterItem, altConfig) {
        const configObj = altConfig || this.config
        if (configObj.filter === null) {
            return null
        } else if (configObj.filter.boperator) {
            const newFilters = []
            for (let i = 0; i < configObj.filter.filters?.length; i++) {
                if (!areEquals(configObj.filter.filters[i], filterItem)) {
                    newFilters.push(configObj.filter.filters[i])
                }
            }
            if (newFilters?.length === 0) {
                configObj.filter = null
                return null
            }
            configObj.filter.filters = newFilters
        } else {
            if (areEquals(configObj.filter, filterItem)) {
                configObj.filter = null
                return null
            }
        }
        return configObj.filter
    }

    getAvailableDrillAttributes() {
        if (this.drillAttributes) {
            return this.drillAttributes.filter(
                (att) => !this.config.usedDrillAttributes.includes(att)
            )
        }
        return []
    }

    setChartStateConfig(config) {
        this.config = config
    }

    setDrillAttributes(attributes) {
        this.drillAttributes = attributes
    }
}
